/**
 * Note metadata snapshot — read/write on QuestionnaireResponse and build from FHIR sources.
 */
import type {
  Appointment,
  ChargeItem,
  Encounter,
  EpisodeOfCare,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { NOTE_METADATA_SNAPSHOT_EXT } from '@unify/fhir-model/constants';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '@/lib/caseloadFhirConstants';
import { displayLabelForEpisodeType } from '@/lib/fhirValueSets/sessionAndEpisodeTypes';
import { getVideoMeetingUrl, setVideoMeetingUrl } from '@unify/fhir-model/resources/appointment';
import { getChargeItemCode } from '@unify/fhir-model/resources/charge-item';
import {
  noteMetadataSnapshotSchema,
  type NoteMetadataBillingCode,
  type NoteMetadataSnapshot,
} from '@/schemas/note-metadata.schema';
import { readEncounterSessionTiming } from '@/lib/encounterSessionTiming';
import { resolveAppointmentModality } from './utils/appointmentModality';
import { getHealthcareServiceIdFromAppointment } from './utils/healthcareServiceNoteTemplate';

const LOC_TYPE = 'LOC';
const ROOM_TYPE = 'ROOM';

function participantHasType(participant: Appointment['participant'][0], code: string): boolean {
  return (
    participant.type?.some((t) =>
      t.coding?.some((c) => c.code?.toUpperCase() === code),
    ) ?? false
  );
}

function formatAttendanceFromAppointment(appointment: Appointment): string | undefined {
  const status = (appointment.status ?? '').toLowerCase();
  if (status === 'fulfilled' || status === 'arrived' || status === 'checked-in') {
    return 'Attended';
  }
  if (status === 'noshow' || status === 'no-show') {
    return 'No-show';
  }
  if (status === 'cancelled') {
    return 'Cancelled';
  }
  return appointment.status ?? undefined;
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

/** Build location list: video URL for telehealth/hybrid; site/room for in-person/hybrid. */
export function buildLocationsFromAppointment(appointment: Appointment): string[] {
  const modality = resolveAppointmentModality(appointment);
  const out: string[] = [];
  const videoUrl = getVideoMeetingUrl(appointment);
  if ((modality === 'telehealth' || modality === 'hybrid') && videoUrl?.trim()) {
    out.push(videoUrl.trim());
  }
  if (modality === 'in-person' || modality === 'hybrid') {
    for (const p of appointment.participant ?? []) {
      if (!participantHasType(p, LOC_TYPE) && !participantHasType(p, ROOM_TYPE)) continue;
      const label = p.actor?.display?.trim();
      if (label) out.push(label);
    }
  }
  return [...new Set(out)];
}

/** Round session duration to one decimal place (Zoom actual length). */
export function roundSessionDurationMinutes(minutes: number): number {
  return Math.round(minutes * 10) / 10;
}

export function isTelehealthLikeModality(modality: NoteMetadataSnapshot['modality']): boolean {
  return modality === 'telehealth' || modality === 'hybrid';
}

/**
 * Default session duration for the note editor:
 * 1. Telehealth/hybrid + Zoom actual on Encounter timing extension (1 decimal)
 * 2. Saved note duration (in-person, or telehealth before Zoom timing is captured)
 * 3. HealthcareService scheduling duration (e.g. 53 for 90837)
 * 4. Encounter timing serviceTypeExpectedMinutes
 * 5. Appointment block length fallback
 */
export function resolveSessionDurationMinutes(input: {
  appointment: Appointment;
  encounter?: Encounter | null;
  savedDurationMinutes?: number;
  healthcareServiceDurationMinutes?: number;
}): number | undefined {
  const modality = resolveAppointmentModality(input.appointment);
  const timing = readEncounterSessionTiming(input.encounter);

  if (isTelehealthLikeModality(modality) && timing?.actualSessionMinutes != null) {
    return roundSessionDurationMinutes(timing.actualSessionMinutes);
  }

  if (input.savedDurationMinutes != null) {
    return input.savedDurationMinutes;
  }

  if (input.healthcareServiceDurationMinutes != null) {
    return input.healthcareServiceDurationMinutes;
  }

  if (timing?.serviceTypeExpectedMinutes != null) {
    return timing.serviceTypeExpectedMinutes;
  }

  return computeDurationMinutesFromAppointment(input.appointment);
}

export function computeDurationMinutesFromAppointment(appointment: Appointment): number | undefined {
  if (appointment.minutesDuration != null && appointment.minutesDuration > 0) {
    return appointment.minutesDuration;
  }
  if (appointment.start && appointment.end) {
    const ms = Date.parse(appointment.end) - Date.parse(appointment.start);
    if (Number.isFinite(ms) && ms > 0) {
      return Math.round(ms / 60_000);
    }
  }
  return undefined;
}

function getRenderingProvidersFromEncounter(encounter: Encounter | null | undefined): NoteMetadataSnapshot['renderingProviders'] {
  if (!encounter?.participant?.length) return undefined;
  const providers = encounter.participant
    .filter((p) => {
      const codes = p.type?.flatMap((t) => t.coding?.map((c) => c.code?.toUpperCase()) ?? []) ?? [];
      return codes.includes('PPRF') || codes.includes('ATND') || !!p.individual?.reference;
    })
    .map((p) => ({
      reference: p.individual?.reference,
      display:
        p.individual?.display?.trim() ||
        p.individual?.reference?.split('/').pop() ||
        'Provider',
    }))
    .filter((p) => p.display);
  return providers.length > 0 ? providers : undefined;
}

function getEpisodeFromAppointment(
  appointment: Appointment,
): NoteMetadataSnapshot['episodeOfCare'] | undefined {
  const ext = appointment.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION);
  const ref = ext?.valueReference?.reference?.trim();
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return {
    reference: ref,
    display: ext?.valueReference?.display?.trim(),
  };
}

function getEpisodeFromEncounter(encounter: Encounter | null | undefined): NoteMetadataSnapshot['episodeOfCare'] {
  const ref = encounter?.episodeOfCare?.[0];
  if (!ref?.reference && !ref?.display) return undefined;
  return {
    reference: ref.reference,
    display: ref.display,
  };
}

function resolveEpisodeOfCareForSnapshot(input: {
  appointment: Appointment;
  encounter?: Encounter | null;
  episodeOfCare?: EpisodeOfCare | null;
}): NoteMetadataSnapshot['episodeOfCare'] {
  const fromResource = getEpisodeFromResource(input.episodeOfCare);
  const base =
    getEpisodeFromEncounter(input.encounter) ??
    getEpisodeFromAppointment(input.appointment) ??
    fromResource;
  if (base && fromResource?.display && !base.display) {
    return { ...base, display: fromResource.display };
  }
  return base;
}

function getEpisodeFromResource(episode: EpisodeOfCare | null | undefined): NoteMetadataSnapshot['episodeOfCare'] {
  if (!episode?.id) return undefined;
  const display = displayLabelForEpisodeType(episode.type?.[0]);
  return { reference: `EpisodeOfCare/${episode.id}`, display };
}

const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';

function getCptCodeFromAppointment(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    const cpt = serviceType.coding?.find((c) => c.system === CPT_SYSTEM)?.code?.trim();
    if (cpt) return cpt;
  }
  return undefined;
}

function getCptDisplayFromAppointment(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    const cpt = serviceType.coding?.find((c) => c.system === CPT_SYSTEM);
    if (cpt?.code?.trim()) {
      return cpt.display?.trim() || serviceType.text?.trim() || undefined;
    }
  }
  for (const serviceType of appointment.serviceType ?? []) {
    if (serviceType.text?.trim()) return serviceType.text.trim();
    const display = serviceType.coding?.find((c) => c.display?.trim())?.display?.trim();
    if (display) return display;
  }
  return undefined;
}

function buildDefaultBillingRow(appointment: Appointment): NoteMetadataSnapshot['billingCodes'] {
  const hsId = getHealthcareServiceIdFromAppointment(appointment);
  const cpt = getCptCodeFromAppointment(appointment);
  const code = cpt ?? 'unspecified';
  return [{
    code,
    display: getCptDisplayFromAppointment(appointment),
    healthcareServiceId: hsId,
    units: 1,
  }];
}

function normalizeBillingRows(
  billingCodes: NoteMetadataSnapshot['billingCodes'],
  legacyUnits: number | undefined,
): NoteMetadataSnapshot['billingCodes'] {
  if (!billingCodes?.length) return undefined;
  return billingCodes.map((row, index) => ({
    ...row,
    units: row.units ?? (index === 0 ? legacyUnits : undefined) ?? 1,
  }));
}

function getBillingFromChargeItems(
  chargeItems: ChargeItem[],
  appointment: Appointment,
): Pick<NoteMetadataSnapshot, 'billingCodes' | 'units'> {
  if (chargeItems.length === 0) {
    return { billingCodes: buildDefaultBillingRow(appointment) };
  }
  const appointmentHsId = getHealthcareServiceIdFromAppointment(appointment);
  const billingCodes = chargeItems
    .map((ci, index) => {
      const code = getChargeItemCode(ci);
      if (!code) return null;
      const units = ci.quantity?.value;
      return {
        code,
        display: ci.code?.coding?.[0]?.display ?? ci.code?.text,
        chargeItemId: ci.id,
        // Select binds to healthcareServiceId — keep appointment HS on the primary row.
        healthcareServiceId: index === 0 ? appointmentHsId : undefined,
        units: units != null && units > 0 ? units : 1,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
  return {
    billingCodes: billingCodes.length > 0 ? billingCodes : buildDefaultBillingRow(appointment),
    units: billingCodes[0]?.units,
  };
}

function buildAppointmentTypeForModality(modality: NoteMetadataSnapshot['modality']): Appointment['appointmentType'] {
  if (modality === 'telehealth') {
    return { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'VR', display: 'Telehealth' }] };
  }
  if (modality === 'hybrid') {
    return {
      coding: [{ system: 'https://unify.health/fhir/CodeSystem/session-modality', code: 'hybrid', display: 'Hybrid' }],
    };
  }
  return { text: 'In-person' };
}

/** Build a metadata snapshot from live FHIR sources (Appointment, Encounter, ChargeItem, etc.). */
export function buildNoteMetadataFromSources(input: {
  appointment: Appointment;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
  episodeOfCare?: EpisodeOfCare | null;
  healthcareServiceDurationMinutes?: number;
}): NoteMetadataSnapshot {
  const { appointment, encounter, chargeItems = [], episodeOfCare } = input;
  const billing = getBillingFromChargeItems(chargeItems, appointment);
  const snapshot: NoteMetadataSnapshot = {
    start: appointment.start ?? new Date().toISOString(),
    end: appointment.end,
    durationMinutes: resolveSessionDurationMinutes({
      appointment,
      encounter,
      healthcareServiceDurationMinutes: input.healthcareServiceDurationMinutes,
    }),
    attendance: formatAttendanceFromAppointment(appointment),
    modality: resolveAppointmentModality(appointment),
    locations: buildLocationsFromAppointment(appointment),
    renderingProviders: getRenderingProvidersFromEncounter(encounter),
    episodeOfCare: resolveEpisodeOfCareForSnapshot({ appointment, encounter, episodeOfCare }),
    ...billing,
    capturedAt: new Date().toISOString(),
  };
  return noteMetadataSnapshotSchema.parse(snapshot);
}

/** Read persisted metadata snapshot from QR extension. */
export function getNoteMetadataFromQR(qr: QuestionnaireResponse): NoteMetadataSnapshot | undefined {
  const ext = qr.extension?.find((e) => e.url === NOTE_METADATA_SNAPSHOT_EXT);
  if (!ext?.valueString) return undefined;
  try {
    const parsed = noteMetadataSnapshotSchema.safeParse(JSON.parse(ext.valueString));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

/** Write metadata snapshot onto QR (returns shallow copy). */
export function setNoteMetadataOnQR(
  qr: QuestionnaireResponse,
  metadata: NoteMetadataSnapshot,
): QuestionnaireResponse {
  const validated = noteMetadataSnapshotSchema.parse(metadata);
  const { billingCodes: _omitBilling, ...snapshotWithoutCpt } = validated;
  void _omitBilling;
  const other = (qr.extension ?? []).filter((e) => e.url !== NOTE_METADATA_SNAPSHOT_EXT);
  return {
    ...qr,
    extension: [
      ...other,
      { url: NOTE_METADATA_SNAPSHOT_EXT, valueString: JSON.stringify(snapshotWithoutCpt) },
    ],
  };
}

/**
 * Resolve metadata for the note editor: prefer saved QR snapshot, else build from sources.
 */
/** Static header fields always reflect the current Appointment (not editable in the note editor). */
export function applyAppointmentStaticFieldsToMetadata(
  metadata: NoteMetadataSnapshot,
  appointment: Appointment,
): NoteMetadataSnapshot {
  return {
    ...metadata,
    start: appointment.start ?? metadata.start,
    end: appointment.end ?? metadata.end,
  };
}

export function resolveNoteMetadataForEditor(input: {
  existingResponse?: QuestionnaireResponse | null;
  appointment: Appointment;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
  episodeOfCare?: EpisodeOfCare | null;
  healthcareServiceDurationMinutes?: number;
}): NoteMetadataSnapshot {
  const fromSources = buildNoteMetadataFromSources(input);
  const saved = input.existingResponse ? getNoteMetadataFromQR(input.existingResponse) : undefined;
  const durationMinutes = resolveSessionDurationMinutes({
    appointment: input.appointment,
    encounter: input.encounter,
    savedDurationMinutes: saved?.durationMinutes,
    healthcareServiceDurationMinutes: input.healthcareServiceDurationMinutes,
  });
  if (saved) {
    const merged: NoteMetadataSnapshot = {
      ...saved,
      durationMinutes: durationMinutes ?? saved.durationMinutes,
      billingCodes: normalizeBillingRows(saved.billingCodes, saved.units) ?? fromSources.billingCodes,
      modality: saved.modality ?? fromSources.modality,
      locations: saved.locations ?? fromSources.locations,
      attendance: saved.attendance ?? fromSources.attendance,
    };
    return applyAppointmentStaticFieldsToMetadata(merged, input.appointment);
  }
  return fromSources;
}

/** Fields that changed between two snapshots (for audit + source sync). */
export function diffNoteMetadata(
  before: NoteMetadataSnapshot | undefined,
  after: NoteMetadataSnapshot,
): string[] {
  const changed: string[] = [];
  const keys: (keyof NoteMetadataSnapshot)[] = [
    'start',
    'end',
    'durationMinutes',
    'attendance',
    'modality',
    'units',
  ];
  for (const key of keys) {
    if (before?.[key] !== after[key]) changed.push(key);
  }
  if (JSON.stringify(before?.locations) !== JSON.stringify(after.locations)) {
    changed.push('locations');
  }
  if (JSON.stringify(before?.renderingProviders) !== JSON.stringify(after.renderingProviders)) {
    changed.push('renderingProviders');
  }
  if (JSON.stringify(before?.billingCodes) !== JSON.stringify(after.billingCodes)) {
    changed.push('billingCodes');
  }
  if (JSON.stringify(before?.episodeOfCare) !== JSON.stringify(after.episodeOfCare)) {
    changed.push('episodeOfCare');
  }
  return changed;
}

/** Map appointment attendance label back to FHIR status when edited. */
export function attendanceLabelToAppointmentStatus(label: string | undefined): Appointment['status'] | undefined {
  const normalized = (label ?? '').trim().toLowerCase();
  if (normalized === 'attended') return 'fulfilled';
  if (normalized === 'no-show') return 'noshow';
  if (normalized === 'cancelled') return 'cancelled';
  return undefined;
}

export function applyMetadataStartEndToAppointment(
  appointment: Appointment,
  metadata: NoteMetadataSnapshot,
): Appointment {
  const next: Appointment = { ...appointment };
  if (metadata.start) next.start = metadata.start;
  if (metadata.end) next.end = metadata.end;
  if (metadata.durationMinutes != null) {
    next.minutesDuration = Math.round(metadata.durationMinutes);
    if (metadata.start && !metadata.end) {
      const endMs = Date.parse(metadata.start) + metadata.durationMinutes * 60_000;
      if (Number.isFinite(endMs)) next.end = new Date(endMs).toISOString();
    }
  }
  const mappedStatus = attendanceLabelToAppointmentStatus(metadata.attendance);
  if (mappedStatus) next.status = mappedStatus;
  if (metadata.modality) {
    next.appointmentType = buildAppointmentTypeForModality(metadata.modality);
  }
  const videoUrl = metadata.locations?.find((l) => isHttpUrl(l));
  if (videoUrl) {
    setVideoMeetingUrl(next, videoUrl);
  }
  return next;
}

export type BillingServiceTypeRef = {
  id: string;
  cptCode: string;
  billingTitle?: string;
};

/** Resolve CPT code for a billing row from saved code or linked HealthcareService. */
export function resolveBillingRowCptCode(
  row: NoteMetadataBillingCode,
  serviceType?: Pick<BillingServiceTypeRef, 'cptCode'>,
): string | undefined {
  const saved = row.code?.trim();
  if (saved && saved !== 'unspecified') return saved;
  const fromService = serviceType?.cptCode?.trim();
  return fromService || undefined;
}

/** Saved note display: units × billing descriptor (CPT code). */
export function formatBillingRowLabel(
  row: NoteMetadataBillingCode,
  serviceType?: Pick<BillingServiceTypeRef, 'cptCode' | 'billingTitle'>,
): string {
  const cpt = resolveBillingRowCptCode(row, serviceType);
  const name = serviceType?.billingTitle?.trim() || row.display?.trim();
  const units = row.units ?? 1;
  if (name && cpt) return `${units} × (${cpt}) ${name}`;
  if (name) return `${units} × ${name}`;
  if (cpt) return `${units} × (${cpt})`;
  return `${units} × —`;
}

/** Fill in CPT codes / HealthcareService ids on billing rows from the catalog before save/display. */
export function enrichBillingCodesWithCpt(
  metadata: NoteMetadataSnapshot,
  serviceTypes: BillingServiceTypeRef[],
): NoteMetadataSnapshot {
  if (!metadata.billingCodes?.length || serviceTypes.length === 0) return metadata;
  const billingCodes = metadata.billingCodes.map((row) => {
    let serviceType = row.healthcareServiceId
      ? serviceTypes.find((st) => st.id === row.healthcareServiceId)
      : undefined;
    if (!serviceType) {
      const cpt = row.code?.trim();
      if (cpt && cpt !== 'unspecified') {
        serviceType = serviceTypes.find((st) => st.cptCode?.trim() === cpt);
      }
    }
    const cpt = resolveBillingRowCptCode(row, serviceType);
    if (!cpt && !serviceType) return row;
    return {
      ...row,
      healthcareServiceId: row.healthcareServiceId ?? serviceType?.id,
      code: cpt ?? row.code,
      display: row.display?.trim() || serviceType?.billingTitle || row.display,
    };
  });
  const parsed = noteMetadataSnapshotSchema.safeParse({ ...metadata, billingCodes });
  return parsed.success ? parsed.data : metadata;
}

export function applyMetadataBillingToChargeItem(
  chargeItem: ChargeItem,
  billingRow: NonNullable<NoteMetadataSnapshot['billingCodes']>[number],
): ChargeItem {
  if (!billingRow.code || billingRow.code === 'unspecified') return chargeItem;
  const next: ChargeItem = { ...chargeItem };
  next.code = {
    ...chargeItem.code,
    text: billingRow.display ?? billingRow.code,
    coding: [{ system: 'http://www.ama-assn.org/go/cpt', code: billingRow.code, display: billingRow.display }],
  };
  if (billingRow.units != null) {
    next.quantity = { ...chargeItem.quantity, value: billingRow.units };
  }
  return next;
}
